import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";

/**
 * Bridge page that the Hostaway calendar widget redirects to:
 *   /checkout/:listingId?numberOfGuests=2&checkInDate=...&checkOutDate=...
 *
 * We immediately forward the guest to Hostaway's hosted booking engine page,
 * preserving all query parameters (dates, guest count). The destination
 * subdomain is the user's Hostaway booking engine URL.
 *
 * If the destination 404s, that means the Hostaway-hosted booking engine
 * has not been activated for this account — that's a Hostaway-dashboard
 * configuration step, not a code issue here.
 */

// Hostaway-hosted booking engine destination. Overridable via env so the user
// can swap to the correct working subdomain (e.g. their custom Hostaway URL)
// without a code change.
const HOSTAWAY_CHECKOUT_BASE =
  process.env.REACT_APP_HOSTAWAY_CHECKOUT_URL ||
  "https://book.oonaflats.com";

export default function CheckoutRedirectPage() {
  const { listingId } = useParams();
  const [searchParams] = useSearchParams();
  const { lang } = useLanguage();
  const [destination, setDestination] = useState("");

  useEffect(() => {
    if (!listingId) return;
    const params = searchParams.toString();
    const url = `${HOSTAWAY_CHECKOUT_BASE}/checkout/${listingId}${
      params ? `?${params}` : ""
    }`;
    setDestination(url);
    // Replace, not push: user shouldn't see this page in browser history
    window.location.replace(url);
  }, [listingId, searchParams]);

  return (
    <div
      data-testid="checkout-redirect-page"
      className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6"
    >
      <div className="text-center max-w-md">
        <Loader2
          className="w-10 h-10 animate-spin text-[#1D1D1F] mx-auto mb-6"
          aria-hidden="true"
        />
        <h1
          className="text-2xl md:text-3xl font-semibold tracking-tight text-[#1D1D1F] mb-3"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {lang === "de"
            ? "Weiterleitung zur sicheren Buchung…"
            : "Redirecting to secure checkout…"}
        </h1>
        <p className="text-sm text-[#86868B] leading-relaxed mb-8">
          {lang === "de"
            ? "Deine Buchung wird über unseren Partner Hostaway abgewickelt. Du wirst in einem Moment automatisch weitergeleitet."
            : "Your booking is processed via our partner Hostaway. You will be redirected automatically in a moment."}
        </p>
        {destination && (
          <p className="text-xs text-[#86868B]/70">
            {lang === "de" ? "Manuell öffnen: " : "Open manually: "}
            <a
              href={destination}
              className="underline hover:text-[#1D1D1F]"
              data-testid="manual-redirect-link"
            >
              {destination}
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
